import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, CheckCircle, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import { gameData, getGameProgress } from '../data/mock';
import { FeedbackForm } from './ui/FeedbackForm';

const VictoryScreen = () => {
  const navigate = useNavigate();
  const [showFeedback, setShowFeedback] = useState(true);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  useEffect(() => {
    const loadProgress = async () => {
      const savedProgress = await getGameProgress();

      // If not all dinosaurs are captured, redirect to map
      if (savedProgress.capturedDinosaurs.length !== gameData.totalDinosaurs) {
        navigate('/map');
      }
    };
    loadProgress();
  }, [navigate]);

  const handleFeedbackSuccess = () => {
    setFeedbackSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-900 via-orange-800 to-red-900 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-96 h-96 bg-yellow-500 rounded-full blur-3xl opacity-30 animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-500 rounded-full blur-3xl opacity-30 animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Content - Scrollable */}
      <div className="relative z-10 min-h-screen py-12 px-6 overflow-y-auto">
        <div className="max-w-4xl w-full mx-auto text-center">
          {/* Trophy animation */}
          <div className="mb-6 flex justify-center">
            <div className="relative">
              <Trophy className="w-24 h-24 text-yellow-300 animate-bounce" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-32 h-32 border-4 border-yellow-300 rounded-full animate-ping opacity-50"></div>
              </div>
            </div>
          </div>

          {/* Victory message */}
          <h1 className="text-5xl md:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-orange-400 to-red-400 mb-4 animate-pulse">
            MISSION
            <br />
            COMPLETE!
          </h1>

          <p className="text-xl md:text-2xl text-yellow-100 mb-6 font-semibold">
            Congratulations! You've captured all {gameData.totalDinosaurs} dinosaurs!
          </p>

          {/* Stats card */}
          <div className="bg-black/60 backdrop-blur-md rounded-2xl p-6 border-2 border-yellow-500/30 mb-8 max-w-2xl mx-auto">
            <h2 className="text-xl font-bold text-yellow-300 mb-4">Mission Summary</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {gameData.locations.map((location) => (
                <div key={location.id} className="flex items-center gap-2 bg-black/40 rounded-lg p-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <div className="text-left">
                    <p className="text-white font-semibold text-sm">{location.dinosaur}</p>
                    <p className="text-gray-400 text-xs">{location.name}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Feedback Section */}
          <div className="bg-black/60 backdrop-blur-md rounded-2xl border-2 border-cyan-500/30 mb-8 max-w-2xl mx-auto overflow-hidden">
            <button
              onClick={() => setShowFeedback(!showFeedback)}
              className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-cyan-400" />
                <span className="text-lg font-bold text-cyan-300">
                  {feedbackSubmitted ? 'Feedback Submitted!' : 'Share Your Feedback'}
                </span>
              </div>
              {showFeedback ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {showFeedback && (
              <div className="px-6 pb-6">
                {feedbackSubmitted ? (
                  <div className="text-center py-4">
                    <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                    <p className="text-gray-300">Thank you for helping us improve DRE World!</p>
                  </div>
                ) : (
                  <>
                    <p className="text-gray-400 text-sm mb-4">
                      Help us improve DRE World with your valuable feedback.
                    </p>
                    <FeedbackForm
                      feedbackType="VICTORY"
                      dinosaursCaptured={gameData.totalDinosaurs}
                      totalDinosaurs={gameData.totalDinosaurs}
                      capturedList={gameData.locations.map(loc => loc.dinosaur)}
                      onSubmitSuccess={handleFeedbackSuccess}
                    />
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer message */}
          <p className="text-yellow-200 text-lg">
            World DRE is safe again thanks to your coding skills!
          </p>
        </div>
      </div>
    </div>
  );
};

export default VictoryScreen;
